import React, { useMemo } from "react";
import { Link } from "react-router-dom";

import {
  formatActorString,
  getActorUiStrings,
  resolveActorLocale,
} from "i18n/actorUi";
import {
  getActorDisplayName,
  getActorProfileHref,
} from "util/actorIdentity";
import type { ActorRef } from "types/actor";
import { cn } from "util/cn";

export interface ActorNameProps {
  actor: ActorRef;
  /** Optional override for the visible label. Defaults to display name (or username). */
  label?: string;
  /** Optional override for the aria-label on the link. */
  ariaLabelOverride?: string;
  /** Extra classes applied to the link. */
  className?: string;
  /**
   * When `true`, render the name without a link wrapper (e.g. for
   * anonymous content, or when the name sits inside another link).
   */
  asPlainText?: boolean;
}

/**
 * Standardized actor name primitive. ALWAYS links to the actor's
 * profile (never to a story) — this is the consistent "name click =
 * profile" half of the Instagram contract.
 *
 * Renders as plain text when the actor is null or no profile slug is
 * available (e.g. anonymous comments).
 *
 * Uses `React.forwardRef` so the rendered element (either the
 * profile `<a>` from `<Link>` or the fallback `<span>`) can be
 * captured by Radix-style `Slot`/`SlotClone` consumers (e.g. when
 * passed as a child to `<TooltipTrigger asChild>` or
 * `<DropdownMenuTrigger asChild>`). Without forwardRef, those
 * surfaces emit a runtime warning and lose focus/tooltip behavior.
 */
const ActorName = React.forwardRef<HTMLElement, ActorNameProps>(
  ({ actor, label, ariaLabelOverride, className, asPlainText }, ref) => {
    const ui = useMemo(() => getActorUiStrings(resolveActorLocale()), []);
    const displayName = label ?? getActorDisplayName(actor);
    const href = getActorProfileHref(actor);

    // A single callback ref that targets either the `<span>` fallback
    // or the `<Link>`-rendered anchor. Both are HTMLElements, so a
    // contravariant callback ref types cleanly without `as unknown`
    // casts.
    const setRef = (node: HTMLElement | null): void => {
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    if (!actor || !href || asPlainText) {
      return (
        <span ref={setRef} className={className}>
          {displayName}
        </span>
      );
    }

    const ariaLabel =
      ariaLabelOverride ??
      formatActorString(ui.openProfileNameAria, displayName);

    return (
      <Link
        ref={setRef}
        to={href}
        aria-label={ariaLabel}
        className={cn(
          "inline-flex max-w-full items-center truncate rounded-sm hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-soc-purple",
          className
        )}
        data-testid="actor-name"
      >
        {displayName}
      </Link>
    );
  }
);

ActorName.displayName = "ActorName";

export default ActorName;
